import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import css from "./EnquiryFormUserDetails.module.scss";
import {
    EmailIcon,
    PeopleIcon,
    PhoneIcon,
    UserIcon,
    BookingIcon
} from "@assets/icons";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Controller, useFormContext } from "react-hook-form";
import { EnquiryFormUserDetailsType } from "./types";
import Input from "@ui/Input";
import InputGroup from "@ui/InputGroup";
import BookedVenue from "@components/BookedVenue";
import ClientOnly from "../../ClientOnly";
import useUser from "@hooks/useUser";
import { IBookedResult } from "@components/BookedVenue/BookedVenue";
import useOpenClose from "@hooks/useOpenClose";
import cl from "classnames";
import { USER_LOCATION_COOKIE, USER_VENUE_ID_COOKIE, USER_VENUE_NAME_COOKIE } from "@helper/constants";
import { getCookieByName } from "@helper/cookies";
import { EnquiryFormContext } from "./EnquiryForm";
import { UserSectionProps } from "./UserSection";
import { getCountry } from "@helper/getConfigValues";
import { isValidPhoneNumber } from 'libphonenumber-js';

const UserSection = React.lazy(() => import('./UserSection'));

function EnquiryFormUserDetails({
    setMasterVenueUpdated,
    redirectAnchor
}: {
    setMasterVenueUpdated: (value: boolean) => void,
    redirectAnchor?: string;
}) {
    const { showMasterVenue, isEngagementVenue, hideGuest, userLoading } = useContext(EnquiryFormContext);
    const {
        control,
        formState: { isValid, errors },
        getValues,
        watch,
    } = useFormContext<EnquiryFormUserDetailsType>();

    const ICON_SIZE = 17;
    const engagementDate = watch("engagementDate");
    const weddingDate = watch("date");

    const areAllFieldsFilled = (): boolean => {
        const requiredFields: Array<keyof EnquiryFormUserDetailsType> = [
            "name",
            "email",
            "phone",
            "date",
        ];

        if (isEngagementVenue) {
            requiredFields.push("engagementDate");
        }

        if (!hideGuest) {
            requiredFields.push("guests");
        }

        const values = getValues();

        // Check if at least one of the venue-related fields is present when showMasterVenue is true
        if (showMasterVenue) {
            const venueFields = ["venueName", "venueId", "regionId"] as Array<keyof EnquiryFormUserDetailsType>;
            const hasVenueField = venueFields.some((field) => !!values[field]);
            if (!hasVenueField) {
                return false;
            }
        }

        return requiredFields.every((field) => !!values[field]);
    };

    const areAllFieldsFilledMemoized = useMemo(() => areAllFieldsFilled(), [getValues(), showMasterVenue, isEngagementVenue, hideGuest]);
    const { isOpen, toggle, close } = useOpenClose(!areAllFieldsFilledMemoized);
    const didAutoCollapse = useRef(false);

    // Collapse only after user details are loaded and all fields are valid, but only once
    useEffect(() => {
        if (!userLoading && areAllFieldsFilledMemoized && !didAutoCollapse.current) {
            close();
            didAutoCollapse.current = true;
        }
    }, [userLoading, areAllFieldsFilledMemoized, close]);

    return (
        <div className={cl(css.container, { [css.collapsed]: !isOpen })}>
            <div className={css.header} onClick={toggle}>
                <span>Your details{!areAllFieldsFilledMemoized && <span className={css.hint}>(incomplete)</span>}</span>
                {isOpen ? <Icon icon={faMinus} /> : <Icon icon={faPlus} />}
            </div>
            <ClientOnly<UserSectionProps> Component={UserSection} redirectAnchor={redirectAnchor} />
            <InputGroup postfix={errors.name
                ? errors.name.message
                : null
            }>
                <UserIcon />
                    <Controller
                        name="name"
                        control={control}
                        rules={{
                            required: "Enter your full name",
                        }}
                        render={({ field }) => (
                            <Input
                                id="enquiry-form-name"
                                placeholder="Name"
                                isInvalid={!!errors.name}
                                data-testid="enquiry-form-user-name"
                                {...field}
                            />
                        )}
                    />
            </InputGroup>
            <InputGroup postfix={errors.email
                ? errors.email.message
                : null
            }>
                <EmailIcon />
                <Controller
                    name="email"
                    control={control}
                    rules={{
                        required: "Email is required",
                        pattern: {
                            value: /^\S+@\S+$/i,
                            message: "Invalid email address",
                        },
                    }}
                    render={({ field }) => (
                        <Input
                        id="enquiry-form-email"
                        placeholder="Email"
                        isInvalid={!!errors.email}
                        data-testid="enquiry-form-user-email"
                        {...field}
                    />
                )}
                />
            </InputGroup>
            <InputGroup postfix={errors.phone
                ? errors.phone.message
                : null
            }>
                <PhoneIcon />
                <Controller
                    name="phone"
                    control={control}
                    rules={{
                        validate: (value) => {
                            if (!value) return "Phone is required";
                            const countryCode = getCountry();
                            if (countryCode === 'au') {
                                return isValidPhoneNumber(value, 'AU') || "Enter a valid Australian phone number";
                            } else if (countryCode === 'uk') {
                                return isValidPhoneNumber(value, 'GB') || "Enter a valid UK phone number";
                            }
                            return isValidPhoneNumber(value) || "Enter a valid phone number";
                        }
                    }}
                    render={({ field }) => (
                        <Input
                            id="enquiry-form-phone"
                            placeholder="Phone"
                            isInvalid={!!errors.phone}
                            data-testid="enquiry-form-user-phone"
                            {...field}
                        />
                    )}
                />
            </InputGroup>

            {isEngagementVenue && <InputGroup postfix={errors.engagementDate
                ? errors.engagementDate.message
                : null
            }>
                <BookingIcon width={ICON_SIZE} height={ICON_SIZE} />
                <Controller
                    name="engagementDate"
                    control={control}
                    rules={{
                        validate: (value) => {
                            if (!isEngagementVenue) {
                                return true;
                            }
                            if (!value) return "Engagement date is required";
                            const selectedDate = new Date(value);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            const weddingDate = getValues("date");
                            if (weddingDate) {
                                const weddingDateObj = new Date(weddingDate);
                                if (selectedDate >= weddingDateObj) {
                                    return "Engagement date must be before wedding date";
                                }
                            }

                            return selectedDate >= today || "Please enter a date in the future";
                        }
                    }}
                    render={({ field }) => (
                        // @ts-ignore-next-line
                        <Input
                            id="enquiry-form-date"
                            className="text-start"
                            isEmpty={!engagementDate}
                            placeholder="Engagement date"
                            isInvalid={!!errors.engagementDate}
                            data-testid="enquiry-form-user-engagement-date"
                            type="date"
                            {...field}
                        />
                    )}
                />
            </InputGroup>}
            <div className={hideGuest ? undefined : css.twoFields}>
                {!hideGuest && <InputGroup className={css.groupHeight} postfix={errors.guests
                    ? errors.guests.message
                    : null
                }>
                    <PeopleIcon />
                    <Controller
                        name="guests"
                        control={control}
                        rules={{
                            required: "Guests is required",
                            max: {
                                value: 10000,
                                message: "Max 10000 guests",
                            },
                            min: {
                                value: 0,
                                message: "Min 0 guests",
                            },
                        }}
                        render={({ field }) => (
                            <Input
                                id="enquiry-form-guests"
                                placeholder="Guests"
                                type="number"
                                isInvalid={!!errors.guests}
                                data-testid="enquiry-form-user-guest-count"
                                {...field}
                            />
                        )}
                    />
                </InputGroup>}
                <InputGroup className={css.groupHeight} postfix={errors.date
                    ? errors.date.message
                    : null
                }>
                    <BookingIcon width={ICON_SIZE} height={ICON_SIZE} />
                    <Controller
                        name="date"
                        control={control}
                        rules={{
                            validate: (value) => {
                                if (!value) return true;
                                const selectedDate = new Date(value);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);

                                const engagementDate = getValues("engagementDate");
                                if (engagementDate && isEngagementVenue) {
                                    const engagementDateObj = new Date(engagementDate);
                                    if (selectedDate <= engagementDateObj) {
                                        return "Wedding date must be after engagement date";
                                    }
                                }

                                return selectedDate >= today || "Please enter a date in the future";
                            }
                        }}
                        render={({ field }) => (
                            // @ts-ignore-next-line
                            <Input
                                id="enquiry-form-date"
                                className={`text-start ${!weddingDate ? 'ew-input-empty' : ''}`}
                                placeholder="Wedding date"
                                isInvalid={!!errors.date}
                                data-testid="enquiry-form-user-wedding-date"
                                type="date"
                                {...field}
                            />
                        )} />
                </InputGroup>
            </div>

            {showMasterVenue && <div>
                <MasterVenueSection setMasterVenueUpdated={setMasterVenueUpdated} />
            </div>}
        </div>
    );
}

function MasterVenueSection({ setMasterVenueUpdated }: { setMasterVenueUpdated: (value: boolean) => void }) {
    const {
        register,
        setValue,
        getValues,
    } = useFormContext<EnquiryFormUserDetailsType>();
    const { user, isLoading } = useUser();
    useEffect(() => {
        if (!!user?.id) {
            !!user.regionId && setValue("regionId", user.regionId);
            !!user.bookedVenueSupplierName && setValue("venueName", user.bookedVenueSupplierName);
            !!user.bookedVenueSupplierId && setValue("venueId", user.bookedVenueSupplierId);
        }
        else {
            const cookieFields: Record<string, string> = {
                regionId: USER_LOCATION_COOKIE,
                venueName: USER_VENUE_NAME_COOKIE,
                venueId: USER_VENUE_ID_COOKIE,
            };

            Object.entries(cookieFields).forEach(([field, cookieName]) => {
                const cookieValue = getCookieByName(cookieName);

                // Parse regionId and venueId as numbers, default to 0
                const parsedValue =
                    field === "regionId" || field === "venueId"
                        ? parseInt(cookieValue || "0", 10)
                        : cookieValue || "";

                setValue(field as keyof EnquiryFormUserDetailsType, parsedValue);
            });
        }
    }, [user]);

    const { venueName, venueId, regionId, date } = getValues() || {};
    const onChangeBookedBenue = useCallback((result: IBookedResult) => {
        if (!!result && !!result.venueSupplierId && user.bookedVenueSupplierId !== result.venueSupplierId) {
            setMasterVenueUpdated(true);
        }
        setValue("venueName", !!result.venueSupplierName ? result.venueSupplierName : '');
        setValue("venueId", !!result.venueSupplierId ? result.venueSupplierId : 0);
        setValue("regionId", !!result.regionId ? result.regionId : 0);
    }, [setValue, user.bookedVenueSupplierId, setMasterVenueUpdated]);

    if (isLoading) {
        return null;
    }
    return (<>
        <BookedVenue
            venueSupplierName={venueName}
            venueSupplierId={venueId}
            regionId={regionId}
            onChange={onChangeBookedBenue}
        />
        <div style={{ display: 'none' }}>
            <Input
                id="enquiry-form-region"
                type="hidden"
                {...register("regionId")}
            />
            <Input
                id="enquiry-form-region-name"
                type="hidden"
                {...register("regionName")}
            />
            <Input
                id="enquiry-form-venue-name"
                type="hidden"
                {...register("venueName")}
            />
            <Input
                id="enquiry-form-venue-id"
                type="hidden"
                {...register("venueId")}
            />
        </div>
    </>)
}

export default EnquiryFormUserDetails;
